import merge from 'lodash/merge';
import { CardDataWithHeroExtras, BadgeConfig } from 'flows/core/types/cards';
import { ItemDefinition, ItemKinds } from 'types';
import { DataForCards } from 'flows/core/hooks/use-data-for-cards';
import { EssentialCards } from 'flows/core/types/layouts';
import { getProviderData } from 'flows/insurance/constants/providers';
import { CLOUDINARY_BASE_URL } from 'flows/insurance/constants';
import { ItemStatuses } from 'constants/statuses';
import { enabledItemDefOfKind, isCompleted, itemOfKind, Badges } from './utils';
import { Item } from 'types/item';
import { Direction } from '@updater/consumer-graph';
import { getUserInfo_getUser_user } from 'types/generated/getUserInfo';
import { COVER360_PLUS_TILE_TITLE } from 'flows/insurance/constants/assurant-copy';
import { getFurtherAlongStatus } from 'utils/item-status-progress';

/* Static */
const DEFAULT_TILE_TITLE = 'Secure renter’s insurance';
const KIND = ItemKinds.INSURANCE;

const PREFERRED_PROVIDER_TEXT = 'Preferred by your property';
const MOVE_OUT_PROVIDER_TEXT =
  'See an instant quote and purchase protection for your new home.';

const INSURANCE_COMMON_STATIC_DATA = {
  subtype: 'SYI',
  icon: 'SHIELD',
  title: DEFAULT_TILE_TITLE,
  action: {
    type: 'internal',
    route: '/insurance/landing',
  },
  completionTimeText: '< 5 min',
} as const;

const INSURANCE_ASSURANT_STATIC_DATA = {
  subtype: 'SYI',
  icon: 'SHIELD',
  title: DEFAULT_TILE_TITLE,
  action: {
    type: 'internal',
    route: '/insurance/assurant',
  },
  description:
    'See an instant quote and purchase protection for your new home.',
} as const;

export const statusToBadge = (
  status?: string,
  tab?: string,
  item?: Item
): BadgeConfig | undefined => {
  if (
    status === ItemStatuses.STARTED &&
    tab !== 'today' &&
    item?.metadata?.viewed === true
  ) {
    return Badges.RECENTLY_VIEWED;
  }
  switch (status) {
    case ItemStatuses.UNVERIFIED:
      return Badges.ALMOST_DONE;
    case ItemStatuses.VALIDATING:
      return Badges.IN_REVIEW;
    case ItemStatuses.COMPLETED:
      return Badges.DONE;
    case ItemStatuses.INFORMATION_REQUESTED:
    case ItemStatuses.INFO_REQUESTED:
      return Badges.NEEDS_MORE;
    default:
      return undefined;
  }
};

export const statusToImportance = (status?: string) => {
  if (status === 'started') {
    return 750_001;
  }
  if (status === 'completed') {
    return 0;
  }
  return 1_000_000;
};

export const makeTodayCardJson = (
  status: ItemStatuses,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  commonData: any,
  payloadKind: 'INITIATE' | 'PROMOTED',
  item?: Item
): CardDataWithHeroExtras => {
  return {
    type: payloadKind,
    data: {
      ...commonData,
      badge: statusToBadge(status, 'today', item),
    },
    heroExtras: {
      heading: 'Get insurance coverage to keep your stuff safe. ',
      ctaText: 'Get started',
    },
    identifier: `syi_${payloadKind.toLowerCase()}`,
    importanceScore: statusToImportance(status),
  };
};

// Properties can satisfy "renters insurance" either through the generic
// proof-of-insurance upload (which updates `proofOfInsuranceItem`) or the
// Assurant buyflow (which only ever updates the separate `insurance` item
// instead). Only one of the two is ever actually written to depending on
// which flow the resident used, so surface whichever has actually
// progressed rather than always defaulting to the proof-of-insurance item's
// own (possibly untouched) status.
export const getCardStatusFromProofItem = (
  proofOfInsuranceItem: Item,
  insuranceItem?: Item
): string => {
  return (
    getFurtherAlongStatus(
      proofOfInsuranceItem?.status,
      insuranceItem?.status
    ) ?? ''
  );
};

/* TODAY CARD */
export const makeTodayTabInsuranceCards = (
  { itemDefinitions, items, user, isGreystarPilotEnabled }: DataForCards,
  plansData?: unknown
): CardDataWithHeroExtras[] => {
  const itemDef = itemDefinitions?.find(enabledItemDefOfKind(KIND));
  const proofOfInsuranceItemDef = itemDefinitions.find(
    enabledItemDefOfKind(ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_INSURANCE)
  );
  const insuranceItems = items.filter(itemOfKind(KIND));
  if (user?.currentMove?.toAddress.ownership === 'OWN') return [];

  const isMoveOut = user?.currentMove?.direction === Direction.move_out;
  if (!plansData) return [];
  if (itemDef) {
    const [proofOfInsuranceItem] = items.filter(
      itemOfKind(ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_INSURANCE)
    );

    const status = proofOfInsuranceItemDef
      ? getCardStatusFromProofItem(
          proofOfInsuranceItem as unknown as Item,
          insuranceItems[0] as unknown as Item
        )
      : insuranceItems[0]?.status ?? '';

    if (isGreystarPilotEnabled) {
      const card = makeTodayCardJson(
        status as ItemStatuses,
        { ...INSURANCE_ASSURANT_STATIC_DATA },
        'PROMOTED',
        insuranceItems[0] as unknown as Item
      );
      const preferredProviderJson = merge(card, {
        data: {
          image: {
            url: itemDef?.page?.logo_id
              ? `${CLOUDINARY_BASE_URL}${encodeURI(itemDef.page.logo_id)}`
              : null,
            alt: 'Assurant',
          },
          description: PREFERRED_PROVIDER_TEXT,
        },
      });
      return [preferredProviderJson];
    }

    const cardJson = makeTodayCardJson(
      status as ItemStatuses,
      { ...INSURANCE_COMMON_STATIC_DATA },
      'PROMOTED',
      insuranceItems[0] as unknown as Item
    );

    const providerData = getProviderData(itemDef?.page?.name || '') || {
      ...itemDef.page,
      logo: itemDef?.page?.logo_id
        ? `${CLOUDINARY_BASE_URL}${encodeURI(itemDef.page.logo_id)}`
        : null,
    };
    const preferredProviderJson = merge(cardJson, {
      data: {
        image: providerData?.logo
          ? { url: providerData.logo, alt: providerData.name }
          : {},
        description: isMoveOut
          ? MOVE_OUT_PROVIDER_TEXT
          : PREFERRED_PROVIDER_TEXT,
      },
    });
    return [preferredProviderJson];
  }

  return [];
};

const essentialsImportanceScore = 1_000_000;
const essentialsType = 'INITIATE';
const essentialsIdentifier = 'syi_initiate';

export const isItemRequired = (
  user: getUserInfo_getUser_user | undefined,
  itemDef: ItemDefinition,
  proofItem: Item
) => {
  if (user?.currentMove?.direction === Direction.move_out) return false;

  return !!proofItem || !!itemDef?.page?.required || !!itemDef?.page?.assurant;
};

/* Essentials Card */
export const makeEssentialsTabInsuranceCards = ({
  itemDefinitions,
  items,
  user,
  isGreystarPilotEnabled,
  insurance,
}: DataForCards): EssentialCards => {
  const itemDef = itemDefinitions.find(enabledItemDefOfKind(KIND));
  const insuranceItem = items.filter(itemOfKind(KIND))[0] as unknown as Item;

  const proofOfInsuranceItemDef = itemDefinitions.find(
    enabledItemDefOfKind(ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_INSURANCE)
  );

  const isUPOMove = Boolean(user?.currentMove?.residentOnboardingEnabled);

  const essentialCardLists: EssentialCards = {
    toDoCards: [],
    completedCards: [],
  };
  if (itemDef && user?.currentMove?.toAddress.ownership !== 'OWN') {
    const proofOfInsuranceItem = items.find(
      itemOfKind(ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_INSURANCE)
    ) as unknown as Item;

    const status = proofOfInsuranceItemDef
      ? getCardStatusFromProofItem(proofOfInsuranceItem, insuranceItem)
      : insuranceItem?.status ?? '';

    if (isGreystarPilotEnabled) {
      return makeAssurantEssentialsCard({ status });
    }

    const required = isItemRequired(user, itemDef, proofOfInsuranceItem);

    const isAssurantCover360Plus =
      insurance?.AssurantEligibility.variant === 'cover360_plus';
    const title = isAssurantCover360Plus
      ? COVER360_PLUS_TILE_TITLE
      : DEFAULT_TILE_TITLE;

    const description = isAssurantCover360Plus
      ? 'Learn more about the policy included with your lease or upload proof of coverage.'
      : isUPOMove
      ? 'Ensure you have the proper insurance coverage and upload proof.'
      : 'Ensure you have the proper insurance coverage.';

    const insuranceStaticData = {
      ...INSURANCE_COMMON_STATIC_DATA,
      title,
      description,
      action: { ...INSURANCE_COMMON_STATIC_DATA.action },
    };

    const targetList = isCompleted({ status }) ? 'completedCards' : 'toDoCards';
    const badge =
      isCompleted({ status }) || proofOfInsuranceItemDef
        ? statusToBadge(status as ItemStatuses, undefined, insuranceItem)
        : undefined;
    const computedCtaText = badge?.variant === 'done' ? 'View task' : undefined;
    essentialCardLists[targetList].push({
      type: essentialsType,
      data: {
        ...insuranceStaticData,
        // TODO: add badge logic back when we decide on implementation for ROB + Assurant
        badge,
        required,
        status,
        ctaText: computedCtaText,
      },
      identifier: `${essentialsIdentifier}${
        isCompleted({ status }) ? '_completed' : ''
      }${insuranceItem ? `_${insuranceItem.id}` : ''}`,
      importanceScore: essentialsImportanceScore,
    });
  }
  return essentialCardLists;
};

export function makeAssurantEssentialsCard({
  status,
}: {
  status: string;
}): EssentialCards {
  const essentialCardLists: EssentialCards = {
    toDoCards: [],
    completedCards: [],
  };
  const targetList = isCompleted({ status }) ? 'completedCards' : 'toDoCards';
  const badge = statusToBadge(status as ItemStatuses);
  essentialCardLists[targetList].push({
    type: essentialsType,
    data: {
      ...INSURANCE_ASSURANT_STATIC_DATA,
      required: true,
      status,
      badge,
      ctaText: badge?.variant === 'done' ? 'View task' : undefined,
    },
    identifier: essentialsIdentifier,
    importanceScore: essentialsImportanceScore,
  });
  return essentialCardLists;
}
